import { useMemo } from 'react';
import { suiteVocab, resolveTags, tagIndex, tagUsage, tagCounts, findTags } from '@battleops/shared';
import { useTasks } from '../context/TaskContext';
import { useProjects } from '../context/ProjectContext';
import { useNotes } from '../context/NoteContext';
import { useLocalCollection } from './useLocal';

// The suite's one tag list (CFO plan 4.1): the books publish tagVocab to opsTags/{slug} (logical `tags`), read-only.
// Pickers offer it first, then the tags already used here; a merged tag reads as its target.

/** { list: opsTags docs, index: slug → doc }. */
export function useSuiteTags() {
  const list = useLocalCollection('tags');
  const index = useMemo(() => tagIndex(list), [list]);
  return { list, index };
}

/** What a picker offers, and the item's tags read through merges. */
export function useTagPicker(tags, localVocab) {
  const { index } = useSuiteTags();
  const vocab = useMemo(() => suiteVocab(index, localVocab || []), [index, localVocab]);
  const shown = useMemo(() => resolveTags(tags || [], index), [tags, index]);
  return { vocab, shown, index };
}

/**
 * What the tag pages read (shared/tagusage): every task, note, project and project file on this device, and the suite
 * list. { tasks, notes, files, projects, tags (slug → doc) }.
 */
export function useTagSources() {
  const { tasks } = useTasks();
  const { notes } = useNotes();
  const { projects } = useProjects();
  const files = useLocalCollection('projectFiles');
  const { index } = useSuiteTags();
  return useMemo(() => ({ tasks: tasks || [], notes: notes || [], files: files || [], projects: projects || [], tags: index }), [tasks, notes, files, projects, index]);
}

/** One tag's page: tagUsage over this device's data. */
export function useTagUsage(tag) {
  const src = useTagSources();
  return useMemo(() => tagUsage(tag, src), [tag, src]);
}

/** The Tags list: the suite list with counts here, and the strays. */
export function useTagCounts() {
  const src = useTagSources();
  return useMemo(() => tagCounts(src), [src]);
}

/** Search: the used tags matching typed text ("#oli" / "olivers"), prefix first. [{ slug, kind, kindLabel, counts }]. */
export function useFindTags(q, n = 4) {
  const counts = useTagCounts();
  return useMemo(() => findTags(q, counts, n), [q, counts, n]);
}
